import { useState } from "react";
import { Download, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SecurityCard } from "@/components/settings/SecurityCard";
import { AccountCard } from "@/components/settings/AccountCard";
import { NotificationsCard } from "@/components/settings/NotificationsCard";
import { DemoResetCard } from "@/components/settings/DemoResetCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, formatApiError } from "@/lib/api";

const Card = ({ title, children, testid }) => (
  <section className="card-surface space-y-4 p-5 sm:p-6" data-testid={testid}>
    <h2 className="text-lg font-bold">{title}</h2>
    {children}
  </section>
);

const ProfileCard = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name);
  const save = async (patch) => {
    try {
      const { data } = await api.patch("/me/profile", patch);
      setUser(data);
      toast.success("Profil enregistré");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Profil" testid="settings-profile-card">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1"><Label htmlFor="name">Nom</Label><Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" /></div>
        <Button onClick={() => save({ name })} className="rounded-full" data-testid="settings-name-save">Enregistrer</Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Langue</Label>
          <Select value={user.locale} onValueChange={(locale) => save({ locale })}>
            <SelectTrigger data-testid="settings-locale-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fr-FR">Français</SelectItem>
              <SelectItem value="en-GB" disabled>English (bientôt)</SelectItem>
              <SelectItem value="es-ES" disabled>Español (bientôt)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Devise</Label>
          <Select value={user.currency} onValueChange={(currency) => save({ currency })}>
            <SelectTrigger data-testid="settings-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["EUR", "CHF", "GBP", "USD"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </Card>
  );
};

const AppearanceCard = () => {
  const { theme, setTheme, reduceMotion, setReduceMotion } = useTheme();
  return (
    <Card title="Apparence & accessibilité" testid="settings-appearance-card">
      <ToggleGroup type="single" value={theme} onValueChange={(v) => v && setTheme(v)} className="justify-start" data-testid="settings-theme-toggle">
        <ToggleGroupItem value="light" data-testid="settings-theme-light">Clair</ToggleGroupItem>
        <ToggleGroupItem value="dark" data-testid="settings-theme-dark">Sombre</ToggleGroupItem>
        <ToggleGroupItem value="system" data-testid="settings-theme-system">Système</ToggleGroupItem>
      </ToggleGroup>
      <label className="flex items-center justify-between gap-4 text-sm">
        <span>Réduire les animations</span>
        <Switch checked={reduceMotion} onCheckedChange={setReduceMotion} data-testid="settings-reduce-motion-switch" />
      </label>
    </Card>
  );
};

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      Object.assign(document.createElement("a"), { href: url, download: "cashmy-mes-donnees.json" }).click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader eyebrow="Compte" title="Réglages" />
      <DemoResetCard />
      <ProfileCard />
      <AppearanceCard />
      <NotificationsCard />
      <AccountCard />
      <SecurityCard />
      <Card title="Mes données (RGPD)" testid="settings-gdpr-card">
        <p className="text-sm text-muted-foreground">Téléchargez une copie de vos données personnelles. La suppression de compte en libre-service arrive bientôt.</p>
        <Button variant="outline" className="rounded-full" onClick={exportData} data-testid="settings-export-button"><Download className="mr-2 h-4 w-4" />Exporter mes données</Button>
      </Card>
      <Button variant="ghost" className="rounded-full text-destructive" onClick={async () => { await logout(); navigate("/login"); }} data-testid="settings-logout-button">
        <LogOut className="mr-2 h-4 w-4" />Se déconnecter
      </Button>
    </div>
  );
}
